/**
 * useRpcTelemetry Hook
 * 
 * Provides real-time RPC health telemetry including latency, TPS, and diagnostics.
 * Tracks both HTTP and WebSocket endpoints separately.
 * Used by the global RPC status widget in the nav.
 * 
 * Accepts optional endpoint overrides to tie telemetry to the active RPC provider.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Connection } from '@solana/web3.js';
import { getSolanaEndpoint, getSolanaWsEndpoint } from '@/lib/solana/config';
import { debug } from "@/lib/debug";

export type RpcStatus = 'healthy' | 'slow' | 'error';
export type WsConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

export interface RpcTelemetryOptions {
  httpEndpoint?: string;
  wsEndpoint?: string;
  /** If false, telemetry will not start polling/WebSocket until enabled */
  enabled?: boolean;
}

export interface LatencySample {
  timestamp: number;
  latency: number;
}

export interface TpsSample {
  timestamp: number;
  tps: number;
  slot: number;
}

export interface LatencyStats {
  current: number;
  p50: number;
  p95: number;
  max: number;
  min: number;
}

export interface RateLimitInfo {
  limit: number | null;
  remaining: number | null;
  lastUpdated: Date | null;
}

export interface RpcDiagnostics {
  version: string | null;
  health: string | null;
  currentSlot: number | null;
  lastChecked: Date | null;
  lastError: string | null;
  lastErrorStatus: number | null;
}

export interface RpcTelemetry {
  // Current state
  status: RpcStatus;
  latency: number | null;
  latencyStats: LatencyStats | null;
  tps: number | null;
  
  // Samples for charts
  latencySamples: LatencySample[];
  tpsSamples: TpsSample[];
  
  // Diagnostics
  diagnostics: RpcDiagnostics;
  
  // Rate limiting
  rateLimit: RateLimitInfo;
  
  // WebSocket status
  wsStatus: WsConnectionStatus;
  
  // Metadata
  httpEndpoint: string;
  wsEndpoint: string;
  maskedHttpEndpoint: string;
  maskedWsEndpoint: string;
  lastUpdated: Date | null;
  isCollecting: boolean;
  
  // Actions
  refresh: () => Promise<void>;
  copyDebugInfo: () => void;
}

const LATENCY_THRESHOLD_SLOW = 1500; // ms
const LATENCY_THRESHOLD_ERROR = 5000; // ms
const MAX_LATENCY_SAMPLES = 20;
const MAX_TPS_SAMPLES = 30;

// Polling intervals
const LATENCY_POLL_INTERVAL = 30000; // 30 seconds
const TPS_POLL_INTERVAL = 90000; // 90 seconds
const DIAGNOSTICS_POLL_INTERVAL = 180000; // 3 minutes

function maskApiKey(url: string | undefined): string {
  if (!url) return '(not configured)';
  try {
    const parsed = new URL(url);
    const params = new URLSearchParams(parsed.search);
    if (params.has('api-key')) {
      params.set('api-key', '***');
    }
    if (params.has('apiKey')) {
      params.set('apiKey', '***');
    }
    if (params.has('api_key')) {
      params.set('api_key', '***');
    }
    parsed.search = params.toString();
    return parsed.toString();
  } catch {
    // If URL parsing fails, do simple regex replacement
    return url.replace(/([?&](?:api[-_]?key)=)[^&]+/gi, '$1***');
  }
}

function calculatePercentile(sortedValues: number[], percentile: number): number {
  if (sortedValues.length === 0) return 0;
  const index = Math.ceil((percentile / 100) * sortedValues.length) - 1;
  return sortedValues[Math.max(0, index)];
}

function calculateStats(samples: LatencySample[]): LatencyStats | null {
  if (samples.length === 0) return null;
  
  const latencies = samples.map(s => s.latency);
  const sorted = [...latencies].sort((a, b) => a - b);
  
  return {
    current: latencies[latencies.length - 1],
    p50: calculatePercentile(sorted, 50),
    p95: calculatePercentile(sorted, 95),
    max: Math.max(...latencies),
    min: Math.min(...latencies),
  };
}

export function useRpcTelemetry(options: RpcTelemetryOptions = {}): RpcTelemetry {
  // Use provided endpoints or fall back to global config
  const httpEndpoint = options.httpEndpoint || getSolanaEndpoint();
  const wsEndpoint = options.wsEndpoint || getSolanaWsEndpoint();
  const enabled = options.enabled ?? true;
  
  const maskedHttpEndpoint = useMemo(() => maskApiKey(httpEndpoint), [httpEndpoint]);
  const maskedWsEndpoint = useMemo(() => maskApiKey(wsEndpoint), [wsEndpoint]);
  
  const connectionRef = useRef<Connection | null>(null);
  const latencySamplesRef = useRef<LatencySample[]>([]);
  const tpsSamplesRef = useRef<TpsSample[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const previousEndpointRef = useRef<string | null>(null);
  const hasEverConnectedRef = useRef(false); // Track if WS has connected before (to avoid "reconnecting" flash on first connect)
  
  const [status, setStatus] = useState<RpcStatus>('healthy');
  const [latency, setLatency] = useState<number | null>(null);
  const [latencyStats, setLatencyStats] = useState<LatencyStats | null>(null);
  const [latencySamples, setLatencySamples] = useState<LatencySample[]>([]);
  const [tps, setTps] = useState<number | null>(null);
  const [tpsSamples, setTpsSamples] = useState<TpsSample[]>([]);
  const [diagnostics, setDiagnostics] = useState<RpcDiagnostics>({
    version: null,
    health: null,
    currentSlot: null,
    lastChecked: null,
    lastError: null,
    lastErrorStatus: null,
  });
  const [rateLimit, setRateLimit] = useState<RateLimitInfo>({
    limit: null,
    remaining: null,
    lastUpdated: null,
  });
  const [wsStatus, setWsStatus] = useState<WsConnectionStatus>('connected'); // Optimistic default to prevent flash
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isCollecting, setIsCollecting] = useState(true);
  
  // Track if endpoint has changed for triggering refetch
  const endpointChangedRef = useRef(false);
  
  // Clear stale data when endpoint changes
  useEffect(() => {
    if (previousEndpointRef.current && previousEndpointRef.current !== httpEndpoint) {
      // Endpoint changed - clear old samples to avoid stale data
      latencySamplesRef.current = [];
      tpsSamplesRef.current = [];
      setLatencySamples([]);
      setTpsSamples([]);
      setLatencyStats(null);
      setIsCollecting(true);
      // Reset connection tracking so we don't flash "reconnecting" for new endpoint
      hasEverConnectedRef.current = false;
      
      // Clear diagnostics when endpoint changes (prevent stale version/health)
      setDiagnostics({
        version: null,
        health: null,
        currentSlot: null,
        lastChecked: null,
        lastError: null,
        lastErrorStatus: null,
      });
      
      // Mark that endpoint changed for refetch effect
      endpointChangedRef.current = true;
    }
    previousEndpointRef.current = httpEndpoint;
  }, [httpEndpoint]);
  
  
  // Initialize connection with explicit wsEndpoint
  useEffect(() => {
    connectionRef.current = new Connection(httpEndpoint, {
      commitment: 'confirmed',
      confirmTransactionInitialTimeout: 60000,
      wsEndpoint: wsEndpoint,
    });
  }, [httpEndpoint, wsEndpoint]);
  
  // Monitor WebSocket status - LAZY: only connect if enabled (popover is open)
  useEffect(() => {
    // Skip WebSocket connection if telemetry is disabled (popover closed)
    if (!enabled) {
      setWsStatus('disconnected');
      return;
    }
    
    // Safety check: don't try to connect if wsEndpoint is undefined or invalid
    if (!wsEndpoint) {
      console.warn('[RpcTelemetry] WS endpoint not configured, skipping WS connection');
      setWsStatus('disconnected');
      return;
    }
    
    // DEV ASSERTION: Verify WS endpoint is correct
    if (import.meta.env.DEV && wsEndpoint.includes('solana-rpc-proxy')) {
      console.error('[RpcTelemetry] INVARIANT VIOLATION: WS endpoint points to HTTP proxy!', wsEndpoint);
      setWsStatus('disconnected');
      return;
    }
    
    let reconnectTimer: number | null = null;
    let keepaliveInterval: number | null = null;
    let disconnectGraceTimer: number | null = null;
    let currentWs: WebSocket | null = null;
    let pingIdCounter = 0;
    
    const KEEPALIVE_INTERVAL_MS = 30000; // Send ping every 30 seconds
    const DISCONNECT_GRACE_MS = 3000; // Wait 3 seconds before showing disconnected
    
    const clearTimers = () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (keepaliveInterval) clearInterval(keepaliveInterval);
      if (disconnectGraceTimer) clearTimeout(disconnectGraceTimer);
      reconnectTimer = null;
      keepaliveInterval = null;
      disconnectGraceTimer = null;
    };
    
    const connectWs = () => {
      if (currentWs?.readyState === WebSocket.OPEN || currentWs?.readyState === WebSocket.CONNECTING) {
        return;
      }
      
      // Clear any pending grace timer when attempting to connect
      if (disconnectGraceTimer) {
        clearTimeout(disconnectGraceTimer);
        disconnectGraceTimer = null;
      }
      
      // Only show "reconnecting" if we've connected before (prevents flash on first open)
      if (hasEverConnectedRef.current) {
        setWsStatus('reconnecting');
      }
      
      try {
        currentWs = new WebSocket(wsEndpoint);
        wsRef.current = currentWs;
        
        currentWs.onopen = () => {
          debug.trace('[RpcTelemetry] WebSocket connected');
          hasEverConnectedRef.current = true;
          setWsStatus('connected');
          
          // Clear any pending disconnect grace timer
          if (disconnectGraceTimer) {
            clearTimeout(disconnectGraceTimer);
            disconnectGraceTimer = null;
          }
          
          // Start keepalive ping interval
          if (keepaliveInterval) clearInterval(keepaliveInterval);
          keepaliveInterval = window.setInterval(() => {
            if (currentWs?.readyState === WebSocket.OPEN) {
              const pingId = `keepalive-${++pingIdCounter}`;
              try {
                currentWs.send(JSON.stringify({
                  jsonrpc: '2.0',
                  id: pingId,
                  method: 'getHealth',
                }));
              } catch (err) {
                console.warn('[RpcTelemetry] Failed to send keepalive ping:', err);
              }
            }
          }, KEEPALIVE_INTERVAL_MS);
        };
        
        currentWs.onmessage = () => {
          // Swallow keepalive responses - we don't need to process them
        };
        
        currentWs.onclose = () => {
          debug.trace('[RpcTelemetry] WebSocket closed, starting grace period');
          
          // Clear keepalive interval
          if (keepaliveInterval) {
            clearInterval(keepaliveInterval);
            keepaliveInterval = null;
          }
          
          // Start grace period before showing "disconnected"
          // This prevents UI flicker if we reconnect quickly
          if (!disconnectGraceTimer) {
            disconnectGraceTimer = window.setTimeout(() => {
              setWsStatus('disconnected');
              disconnectGraceTimer = null;
            }, DISCONNECT_GRACE_MS);
          }
          
          // Attempt reconnect after delay
          reconnectTimer = window.setTimeout(connectWs, 2000);
        };
        
        currentWs.onerror = (err) => {
          console.warn('[RpcTelemetry] WebSocket error:', err);
        };
      } catch {
        setWsStatus('disconnected');
        reconnectTimer = window.setTimeout(connectWs, 5000);
      }
    };
    
    connectWs();
    
    return () => {
      clearTimers();
      if (currentWs) {
        currentWs.onclose = null;
        currentWs.close();
      }
    };
  }, [wsEndpoint, enabled]);
  
  // Measure latency and capture rate limit headers
  const measureLatency = useCallback(async () => {
    const start = performance.now();
    try {
      const response = await fetch(httpEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 'latency-check',
          method: 'getLatestBlockhash',
          params: [{ commitment: 'confirmed' }],
        }),
      });
      
      const elapsed = Math.round(performance.now() - start);
      
      // Check for origin blocked error (FORBIDDEN_ORIGIN)
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          if (errorData?.code === 'FORBIDDEN_ORIGIN') {
            const blockedOrigin = errorData?.blockedOrigin || window.location.origin;
            const errorMessage = `RPC blocked by origin allowlist. Add this domain to the proxy allowlist: ${blockedOrigin}`;
            console.error('[useRpcTelemetry] Origin blocked:', errorMessage);
            
            setStatus('error');
            setDiagnostics(prev => ({
              ...prev,
              lastError: errorMessage,
              lastErrorStatus: 403,
              lastChecked: new Date(),
            }));
            return;
          }
        } catch {
          // Failed to parse JSON, fall through to generic error handling
        }
      }
      
      // Extract rate limit headers
      const limitHeader = response.headers.get('X-RateLimit-Limit');
      const remainingHeader = response.headers.get('X-RateLimit-Remaining');
      
      if (limitHeader || remainingHeader) {
        setRateLimit({
          limit: limitHeader ? parseInt(limitHeader, 10) : null,
          remaining: remainingHeader ? parseInt(remainingHeader, 10) : null,
          lastUpdated: new Date(),
        });
      }
      
      // Update status based on response and latency
      let newStatus: RpcStatus = 'healthy';
      if (!response.ok) {
        newStatus = 'error';
        setDiagnostics(prev => ({
          ...prev,
          lastError: `HTTP ${response.status}`,
          lastErrorStatus: response.status,
        }));
      } else if (elapsed >= LATENCY_THRESHOLD_ERROR) {
        newStatus = 'error';
      } else if (elapsed >= LATENCY_THRESHOLD_SLOW) {
        newStatus = 'slow';
      }
      
      // Add sample to ring buffer
      const sample: LatencySample = { timestamp: Date.now(), latency: elapsed };
      latencySamplesRef.current = [
        ...latencySamplesRef.current.slice(-MAX_LATENCY_SAMPLES + 1),
        sample,
      ];
      
      setLatency(elapsed);
      setStatus(newStatus);
      setLatencySamples([...latencySamplesRef.current]);
      setLatencyStats(calculateStats(latencySamplesRef.current));
      setLastUpdated(new Date());
      setIsCollecting(latencySamplesRef.current.length < 3);
      
      // Clear error state on success
      if (response.ok) {
        setDiagnostics(prev => ({ ...prev, lastError: null, lastErrorStatus: null }));
      }
    } catch (error) {
      console.error('[useRpcTelemetry] Latency check failed:', error);
      setStatus('error');
      
      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setDiagnostics(prev => ({ 
        ...prev, 
        lastError: errorMessage,
        lastChecked: new Date(),
      }));
    }
  }, [httpEndpoint]);
  
  // Fetch TPS from recent performance samples
  const fetchTps = useCallback(async () => {
    if (!connectionRef.current) return;
    
    try {
      const samples = await connectionRef.current.getRecentPerformanceSamples(30);
      
      if (samples.length > 0) {
        const tpsSampleList: TpsSample[] = samples.map((sample, index) => ({
          timestamp: Date.now() - (index * 60000),
          tps: Math.round(sample.numTransactions / sample.samplePeriodSecs),
          slot: sample.slot,
        })).reverse();
        
        tpsSamplesRef.current = tpsSampleList.slice(-MAX_TPS_SAMPLES);
        setTpsSamples([...tpsSamplesRef.current]);
        
        if (tpsSampleList.length > 0) {
          setTps(tpsSampleList[tpsSampleList.length - 1].tps);
        }
      }
    } catch (error) {
      console.error('[useRpcTelemetry] TPS fetch failed:', error);
    }
  }, []);
  
  // Fetch health via raw RPC call
  const fetchHealth = useCallback(async (): Promise<{ health: string | null; error?: string; status?: number }> => {
    try {
      const response = await fetch(httpEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'getHealth',
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Failed to read response');
        return { 
          health: null, 
          error: `HTTP ${response.status}: ${errorText.slice(0, 200)}`,
          status: response.status,
        };
      }
      
      const data = await response.json();
      return { health: data.result || (data.error ? 'unhealthy' : null) };
    } catch (err) {
      return { 
        health: null, 
        error: err instanceof Error ? err.message : 'Network error',
      };
    }
  }, [httpEndpoint]);
  
  // Fetch diagnostics
  const fetchDiagnostics = useCallback(async () => {
    if (!connectionRef.current) return;
    
    try {
      const [versionResult, healthResult, slotResult] = await Promise.allSettled([
        connectionRef.current.getVersion(),
        fetchHealth(),
        connectionRef.current.getSlot(),
      ]);
      
      const healthData = healthResult.status === 'fulfilled' ? healthResult.value : null;
      
      setDiagnostics(prev => ({
        version: versionResult.status === 'fulfilled' 
          ? versionResult.value['solana-core'] || null 
          : null,
        health: healthData?.health ?? null,
        currentSlot: slotResult.status === 'fulfilled' ? slotResult.value : null,
        lastChecked: new Date(),
        lastError: healthData?.error ?? prev.lastError,
        lastErrorStatus: healthData?.status ?? prev.lastErrorStatus,
      }));
    } catch (error) {
      console.error('[useRpcTelemetry] Diagnostics fetch failed:', error);
    }
  }, [fetchHealth]);
  
  // Re-fetch diagnostics when endpoint changes and telemetry is enabled
  useEffect(() => {
    if (!enabled) return;
    if (!endpointChangedRef.current) return;
    
    // Reset the flag
    endpointChangedRef.current = false;
    
    // Delay to allow connectionRef to update first
    const timeout = setTimeout(() => {
      if (connectionRef.current) {
        fetchDiagnostics();
      }
    }, 200);
    
    return () => clearTimeout(timeout);
  }, [httpEndpoint, enabled, fetchDiagnostics]);
  
  // Combined refresh
  const refresh = useCallback(async () => {
    await Promise.all([
      measureLatency(),
      fetchTps(),
      fetchDiagnostics(),
    ]);
  }, [measureLatency, fetchTps, fetchDiagnostics]);
  
  // Copy debug info
  const copyDebugInfo = useCallback(() => {
    const debugInfo = {
      httpEndpoint: maskedHttpEndpoint,
      wsEndpoint: maskedWsEndpoint,
      status,
      wsStatus,
      latency,
      latencyStats,
      rateLimit,
      lastUpdated: lastUpdated?.toISOString(),
      diagnostics,
      recentLatencySamples: latencySamplesRef.current.slice(-10).map(s => ({
        timestamp: new Date(s.timestamp).toISOString(),
        latency: s.latency,
      })),
      tpsSummary: tpsSamplesRef.current.length > 0 ? {
        current: tpsSamplesRef.current[tpsSamplesRef.current.length - 1]?.tps,
        samples: tpsSamplesRef.current.length,
        avgTps: Math.round(
          tpsSamplesRef.current.reduce((sum, s) => sum + s.tps, 0) / tpsSamplesRef.current.length
        ),
      } : null,
      collectedAt: new Date().toISOString(),
    };
    
    navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
  }, [maskedHttpEndpoint, maskedWsEndpoint, status, wsStatus, latency, latencyStats, rateLimit, lastUpdated, diagnostics]);
  
  // Track if we've done the initial fetch (for showing latency on first load)
  const didInitialFetchRef = useRef(false);
  
  // Initial latency fetch on mount - runs ONCE regardless of enabled state
  // This ensures we show latency immediately on page load
  useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    
    // Small delay to let connection initialize
    const initialTimeout = setTimeout(() => {
      measureLatency();
    }, 100);
    
    return () => clearTimeout(initialTimeout);
  }, [measureLatency]);
  
  // Setup polling intervals - LAZY: only poll when enabled (popover is open)
  useEffect(() => {
    if (!enabled) return;
    
    // Fetch all telemetry when popover opens
    const initialTimeout = setTimeout(() => {
      measureLatency();
      fetchTps();
      fetchDiagnostics();
    }, 100);
    
    // Set up polling
    const latencyInterval = setInterval(measureLatency, LATENCY_POLL_INTERVAL);
    const tpsInterval = setInterval(fetchTps, TPS_POLL_INTERVAL);
    const diagnosticsInterval = setInterval(fetchDiagnostics, DIAGNOSTICS_POLL_INTERVAL);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(latencyInterval);
      clearInterval(tpsInterval);
      clearInterval(diagnosticsInterval);
    };
  }, [measureLatency, fetchTps, fetchDiagnostics, enabled]);
  
  return {
    status,
    latency,
    latencyStats,
    tps,
    latencySamples,
    tpsSamples,
    diagnostics,
    rateLimit,
    wsStatus,
    httpEndpoint,
    wsEndpoint,
    maskedHttpEndpoint,
    maskedWsEndpoint,
    lastUpdated,
    isCollecting,
    refresh,
    copyDebugInfo,
  };
}
